import { useEffect, useMemo, useState } from "react";
import "./ConfigPanelPage.css";
import useApiBase from "./api/useApiBase";
import AuthGate from "./auth/AuthGate";
import useAuthSession from "./auth/useAuthSession";
import usePageTitle from "./usePageTitle";

const itemKey = (item) => `${item.scope}:${item.line_no ?? "global"}:${item.key}`;

const formatItemValue = (item) => {
  if (!item) {
    return "";
  }
  if (item.kind === "duration" && item.value && typeof item.value === "object") {
    if (item.value.seconds !== undefined && item.value.seconds !== null) {
      return String(item.value.seconds);
    }
  }
  if (item.kind === "json") {
    try {
      return JSON.stringify(item.value ?? null, null, 2);
    } catch {
      return String(item.value ?? "");
    }
  }
  if (item.kind === "boolean") {
    return item.value ? "true" : "false";
  }
  if (item.value === null || item.value === undefined) {
    return "";
  }
  return String(item.value);
};

const parseItemValue = (item, draftValue) => {
  const text = typeof draftValue === "string" ? draftValue.trim() : draftValue;
  if (item.kind === "boolean") {
    return String(text).toLowerCase() === "true";
  }
  if (item.kind === "integer") {
    if (text === "") {
      return item.nullable ? null : 0;
    }
    return Number.parseInt(text, 10);
  }
  if (item.kind === "number" || item.kind === "duration") {
    if (text === "") {
      return item.nullable ? null : 0;
    }
    return Number(text);
  }
  if (item.kind === "json") {
    if (text === "") {
      return item.nullable ? null : {};
    }
    return JSON.parse(text);
  }
  if (text === "") {
    return item.nullable ? null : "";
  }
  return draftValue;
};

const formatValueLabel = (item) => {
  if (item.kind === "duration") {
    if (item.value && typeof item.value === "object" && item.value.display) {
      return item.value.display;
    }
    if (typeof item.value === "number") {
      return `${item.value}s`;
    }
  }
  if (item.kind === "json") {
    try {
      return JSON.stringify(item.value ?? null);
    } catch {
      return String(item.value ?? "");
    }
  }
  if (item.kind === "boolean") {
    return item.value ? "true" : "false";
  }
  if (item.value === null || item.value === undefined) {
    return "null";
  }
  return String(item.value);
};

function ConfigRow({ item, draftValue, saving, saveError, onChange, onSave }) {
  const inputId = `${itemKey(item)}-input`;
  const commonProps = {
    id: inputId,
    value: draftValue,
    onChange: (event) => onChange(item, event.target.value),
  };

  return (
    <div className="config-row">
      <div className="config-row-main">
        <div className="config-row-header">
          <div>
            <div className="config-row-key">{item.key}</div>
            <div className="config-row-meta">
              <span className="config-pill">{item.scope}</span>
              {item.line_no !== undefined && item.line_no !== null && (
                <span className="config-pill config-pill-muted">line {item.line_no}</span>
              )}
              <span className="config-pill config-pill-source">{item.source}</span>
              <span className="config-pill config-pill-kind">{item.kind}</span>
              {item.requires_restart ? (
                <span className="config-pill config-pill-warning">restart</span>
              ) : null}
            </div>
          </div>
          <div className="config-row-value-preview">{formatValueLabel(item)}</div>
        </div>

        <div className="config-row-input-wrap">
          {item.kind === "boolean" ? (
            <select {...commonProps} className="config-input config-input-select" disabled={!item.editable || saving}>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          ) : item.kind === "json" ? (
            <textarea
              {...commonProps}
              className="config-input config-input-textarea"
              rows={Math.max(4, Math.min(10, String(draftValue ?? "").split("\n").length))}
              disabled={!item.editable || saving}
            />
          ) : (
            <input
              {...commonProps}
              className="config-input"
              type={item.kind === "integer" || item.kind === "number" || item.kind === "duration" ? "number" : "text"}
              step={item.kind === "number" || item.kind === "duration" ? "any" : undefined}
              disabled={!item.editable || saving}
            />
          )}
        </div>
      </div>

      <div className="config-row-footer">
        <div className="config-row-status">
          {item.editable ? "Editable" : "Read only"}
          {saveError ? <span className="config-row-error">{saveError}</span> : null}
        </div>
        <button
          type="button"
          className="config-save-btn"
          disabled={!item.editable || saving}
          onClick={() => onSave(item)}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

function ConfigSection({
  title,
  subtitle,
  items,
  drafts,
  savingMap,
  saveErrors,
  onChange,
  onSave,
  isOpen,
  onToggle,
}) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section className="config-section">
      <div className="config-section-heading">
        <div>
          <h2>{title}</h2>
          {subtitle ? <p>{subtitle}</p> : null}
        </div>
        <div className="config-section-actions">
          <div className="config-section-count">{items.length} values</div>
          {onToggle ? (
            <button type="button" className="config-toggle-btn" onClick={onToggle}>
              {isOpen ? "Hide" : "Show"}
            </button>
          ) : null}
        </div>
      </div>
      {isOpen ? (
        <div className="config-grid">
          {items.map((item) => {
            const key = itemKey(item);
            return (
              <ConfigRow
                key={key}
                item={item}
                draftValue={drafts[key] ?? formatItemValue(item)}
                saving={Boolean(savingMap[key])}
                saveError={saveErrors[key]}
                onChange={onChange}
                onSave={onSave}
              />
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function ConfigPanelPage() {
  usePageTitle("Parameters Panel");
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drafts, setDrafts] = useState({});
  const [savingMap, setSavingMap] = useState({});
  const [saveErrors, setSaveErrors] = useState({});
  const [refreshCount, setRefreshCount] = useState(0);
  const [search, setSearch] = useState("");
  const [banner, setBanner] = useState("");
  const [showGlobal, setShowGlobal] = useState(false);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [openLines, setOpenLines] = useState({});
  const apiBase = useApiBase();
  const auth = useAuthSession(apiBase);

  useEffect(() => {
    if (!auth.authChecked || !auth.isAuthenticated) {
      return;
    }

    let active = true;

    const fetchConfig = async () => {
      try {
        const response = await auth.fetchWithAuth("/api/config");
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }
        const data = await response.json();
        if (!active) {
          return;
        }
        setPayload(data);
        setError("");
        setBanner("");
        const nextDrafts = {};
        [...(data?.global_items ?? []), ...(data?.line_configs ?? []).flatMap((line) => line.items ?? [])].forEach((item) => {
          nextDrafts[itemKey(item)] = formatItemValue(item);
        });
        setDrafts((prev) => ({ ...nextDrafts, ...prev }));
      } catch (fetchError) {
        if (active) {
          setError(fetchError instanceof Error ? fetchError.message : "Unknown error");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchConfig();

    return () => {
      active = false;
    };
  }, [auth.authChecked, auth.isAuthenticated, auth.fetchWithAuth, refreshCount]);

  const normalizedSearch = search.trim().toLowerCase();

  const globalItems = useMemo(() => {
    const items = Array.isArray(payload?.global_items) ? payload.global_items : [];
    if (!normalizedSearch) {
      return items;
    }
    return items.filter((item) => {
      const haystack = `${item.key} ${item.source} ${item.kind} ${formatValueLabel(item)}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [normalizedSearch, payload]);

  const lineConfigs = useMemo(() => {
    const lines = Array.isArray(payload?.line_configs) ? payload.line_configs : [];
    if (!normalizedSearch) {
      return lines;
    }
    return lines
      .map((line) => ({
        ...line,
        items: (line.items ?? []).filter((item) => {
          const haystack = `${line.line_no} ${item.key} ${item.source} ${item.kind} ${formatValueLabel(item)}`.toLowerCase();
          return haystack.includes(normalizedSearch);
        }),
      }))
      .filter((line) => line.items.length > 0);
  }, [normalizedSearch, payload]);

  const updateDraft = (item, value) => {
    setDrafts((prev) => ({
      ...prev,
      [itemKey(item)]: value,
    }));
  };

  const saveItem = async (item) => {
    const key = itemKey(item);
    setSavingMap((prev) => ({ ...prev, [key]: true }));
    setSaveErrors((prev) => ({ ...prev, [key]: "" }));
    try {
      const draftValue = drafts[key] ?? formatItemValue(item);
      const value = parseItemValue(item, draftValue);
      const response = await auth.fetchWithAuth("/api/config/override", {
        method: "POST",
        body: {
          key: item.key,
          value,
          scope: item.scope,
          line_no: item.line_no ?? null,
        },
      });
      if (!response.ok) {
        const detail = await response.json().catch(() => null);
        throw new Error(detail?.detail || `Save failed: ${response.status}`);
      }
      setBanner(`Saved ${item.key}`);
      setRefreshCount((current) => current + 1);
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : "Save failed";
      setSaveErrors((prev) => ({ ...prev, [key]: message }));
    } finally {
      setSavingMap((prev) => ({ ...prev, [key]: false }));
    }
  };

  const snapshots = Array.isArray(payload?.recent_snapshots) ? payload.recent_snapshots : [];
  const toggleLine = (lineNo) => {
    setOpenLines((prev) => ({
      ...prev,
      [lineNo]: !prev[lineNo],
    }));
  };

  return (
    <AuthGate auth={auth} title="Config Panel Login">
      <div className="config-page">
        <div className="config-shell">
          <header className="config-hero">
            <div>
              <p className="config-eyebrow">Runtime configuration</p>
              <h1>Parameters Panel</h1>
              <p className="config-hero-copy">
                Inspect the resolved app config, edit runtime overrides, and keep an audit trail of every saved snapshot.
              </p>
            </div>
            <div className="config-hero-actions">
              <button type="button" className="config-secondary-btn" onClick={() => setRefreshCount((value) => value + 1)}>
                Refresh
              </button>
              <button type="button" className="config-secondary-btn" onClick={() => window.location.assign("/")}>
                Back to app
              </button>
            </div>
          </header>

          <div className="config-toolbar">
            <input
              type="search"
              className="config-search"
              placeholder="Search keys, values, or sources"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <div className="config-toolbar-meta">
              <span>{payload?.server_name || "unknown server"}</span>
              <span>{payload?.generated_at ? new Date(payload.generated_at).toLocaleString() : "not loaded"}</span>
            </div>
          </div>

          {banner ? <div className="config-banner config-banner-success">{banner}</div> : null}
          {error ? <div className="config-banner config-banner-error">{error}</div> : null}
          {loading ? <div className="config-banner">Loading config...</div> : null}

          <ConfigSection
            title="Global config"
            subtitle="Resolved values from config.py, .env, and runtime overrides."
            items={globalItems}
            drafts={drafts}
            savingMap={savingMap}
            saveErrors={saveErrors}
            onChange={updateDraft}
            onSave={saveItem}
            isOpen={showGlobal}
            onToggle={() => setShowGlobal((prev) => !prev)}
          />

          <section className="config-section">
            <div className="config-section-heading">
              <div>
                <h2>Line config</h2>
                <p>Values merged from LINE_CONFIGS_JSON (deployments/{`{SERVER_NAME}`}/deployment.json) and runtime overrides.</p>
              </div>
              <div className="config-section-actions">
                <div className="config-section-count">{lineConfigs.length} lines</div>
              </div>
            </div>

            <div className="config-line-list">
              {lineConfigs.map((line) => (
                <div key={line.line_no} className="config-line-card">
                  <div className="config-line-card-head">
                    <div>
                      <h3>Line {line.line_no}</h3>
                      <p>
                        Type: {line.line_type || "unknown"} {line.active_order_no ? `• active order ${line.active_order_no}` : ""}
                      </p>
                    </div>
                    <div className="config-section-actions">
                      <div className="config-section-count">{line.items.length} values</div>
                      <button
                        type="button"
                        className="config-toggle-btn"
                        onClick={() => toggleLine(line.line_no)}
                      >
                        {openLines[line.line_no] ? "Hide" : "Show"}
                      </button>
                    </div>
                  </div>
                  {openLines[line.line_no] ? (
                    <div className="config-grid">
                      {line.items.map((item) => {
                        const key = itemKey(item);
                        return (
                          <ConfigRow
                            key={key}
                            item={item}
                            draftValue={drafts[key] ?? formatItemValue(item)}
                            saving={Boolean(savingMap[key])}
                            saveError={saveErrors[key]}
                            onChange={updateDraft}
                            onSave={saveItem}
                          />
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </section>

          <section className="config-section">
            <div className="config-section-heading">
              <div>
                <h2>Recent snapshots</h2>
                <p>Latest configuration records saved at startup or after a change.</p>
              </div>
              <div className="config-section-actions">
                <button type="button" className="config-toggle-btn" onClick={() => setShowSnapshots((prev) => !prev)}>
                  {showSnapshots ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            {showSnapshots ? (
              <div className="config-snapshot-list">
                {snapshots.length === 0 ? (
                  <div className="config-empty">No snapshots recorded yet.</div>
                ) : (
                  snapshots.map((snapshot) => (
                    <article key={snapshot.id} className="config-snapshot-card">
                      <div className="config-snapshot-top">
                        <strong>{snapshot.scope}</strong>
                        <span>{snapshot.created_at ? new Date(snapshot.created_at).toLocaleString() : "unknown"}</span>
                      </div>
                      <div className="config-snapshot-meta">
                        <span>line {snapshot.line_no ?? "-"}</span>
                        <span>order {snapshot.production_order_no ?? "-"}</span>
                        <span>{snapshot.changed_key ? `changed ${snapshot.changed_key}` : snapshot.source}</span>
                      </div>
                    </article>
                  ))
                )}
              </div>
            ) : null}
          </section>
        </div>
      </div>
    </AuthGate>
  );
}

export default ConfigPanelPage;
